import type { FC } from 'react'

import { ErrorFilled16, InfoFilled16 } from '@sok/design-system'
import { Notification } from 'components/common/Containers/Notification'
import { useTranslation } from 'react-i18next'
import { openDeliveryModal, storeAvailabilityFilterVar } from 'services/Delivery/cache'
import styled, { useTheme } from 'styled-components'
import { DeliverySelectionStage } from 'types/global'
import { useStoreId } from 'utils/hooks/store/use-store-id'

export const AlcoholAlert: FC<{ isEnabled?: boolean; isHomeDelivery?: boolean }> = ({
  isEnabled,
  isHomeDelivery,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (isHomeDelivery) {
    return (
      <div data-test-id="alcohol-home-delivery-alert-box">
        <Notification
          color={theme.colors.warning50}
          textColor={theme.colors.warning900}
          text={t('Alcoholic items are only available for pickup')}
          icon={<ErrorFilled16 />}
        />
      </div>
    )
  }

  if (!isEnabled) {
    return (
      <div data-test-id="alcohol-not-enabled-alert-box">
        <Notification
          color={theme.colors.warning50}
          textColor={theme.colors.warning900}
          text={t('Product not in online store')}
          icon={<ErrorFilled16 />}
        />
      </div>
    )
  }

  return (
    <div data-test-id="alcohol-pickup-alert-box">
      <Notification
        color={theme.colors.warning50}
        textColor={theme.colors.warning900}
        text={t('You can order alcohol products between 9-21 for pickup')}
        icon={<ErrorFilled16 />}
      />
    </div>
  )
}

const handleOpenDeliveryModal = (): void => {
  storeAvailabilityFilterVar(true)
  openDeliveryModal(DeliverySelectionStage.SELECT_METHOD_AND_STORE)
}

export const NotInCurrentStoreAlert: FC = () => {
  const { selectedStoreId } = useStoreId()
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <div data-test-id="product-not-in-current-store-alert-box">
      <Notification
        actionHandler={handleOpenDeliveryModal}
        button
        buttonText={selectedStoreId ? t`Change store` : t`Choose store`}
        color={theme.colors.warning50}
        icon={<ErrorFilled16 />}
        text={t`Product not in current store`}
        textColor={theme.colors.warning900}
      />
    </div>
  )
}

const _NoSelectedStorePriceAlert: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()

  return (
    <aside className={className}>
      <InfoFilled16 />
      <span data-test-id="no-selected-store-price-alert">{t`Choose store to see price`}</span>
    </aside>
  )
}

export const NoSelectedStorePriceAlert = styled(_NoSelectedStorePriceAlert)(
  ({ theme }) => theme.variants.body2.regular,
  ({ theme }) => ({
    color: theme.colors.info500,

    '.icon': {
      marginRight: theme.spacings.xxSmall,
      marginTop: '-0.2em' /** Make the icon nicely centered */,
      verticalAlign: 'middle',
    },
  }),
)
